<script setup lang="ts">
    import { type PropType, computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    const { t: $t } = useI18n({ useScope: 'global' })

    const props = defineProps({
        page: {
            type: Number,
            required: true,
        },
        total: {
            type: Number,
            required: true,
        },
        limit: {
            type: Number,
            required: true,
        },
        limitSteps: {
            type: Array as PropType<number[]>,
            default: undefined,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    })

    const emit = defineEmits<{
        'update:page': [page: number]
        'update:limit': [limit: number]
    }>()

    const pageCount = computed(() =>
        props.total ? Math.ceil(props.total / props.limit) : 0,
    )

    const hasPrevDisabled = computed(() => props.disabled || props.page <= 1)

    const hasNextDisabled = computed(
        () => props.disabled || props.page >= pageCount.value,
    )

    const hasLimitModel = computed({
        get: () => props.limit,
        set: (value) => emit('update:limit', value),
    })
</script>

<template>
    <div class="pk-pagination mt-auto grid grid-cols-3 gap-md pt-24">
        <div class="flex items-center">
            <label class="text-12">{{ $t('label.itemsPerPage') }}:</label>
            <select
                v-if="limitSteps"
                v-model="hasLimitModel"
                :disabled="disabled"
                class="pk-pagination__limit">
                <option v-for="step in limitSteps" :key="step" :value="step">
                    {{ step }}
                </option>
            </select>
            <span v-else class="ml-8 text-12 font-semibold">{{ limit }}</span>
        </div>
        <div class="flex justify-center items-center">
            <VvButtonGroup class="flex-nowrap">
                <VvButton
                    :title="$t('action.prev')"
                    :disabled="hasPrevDisabled"
                    icon="ri:arrow-left-s-line"
                    modifiers="action-quiet"
                    @click.stop="emit('update:page', page - 1)" />
                <button
                    class="rounded border border-surface-3 px-14 py-4 text-center text-12 font-bold"
                    type="button">
                    {{ page }}
                </button>
                <VvButton
                    :title="$t('action.next')"
                    :disabled="hasNextDisabled"
                    icon="ri:arrow-right-s-line"
                    modifiers="action-quiet"
                    @click.stop="emit('update:page', page + 1)" />
            </VvButtonGroup>
            <small v-if="pageCount" class="ml-6 text-12 whitespace-nowrap">
                {{ $t('label.totalPages', { total: pageCount }) }}
            </small>
        </div>
        <div class="flex text-12 justify-end items-center whitespace-nowrap">
            {{ $t('label.totalItemsCount', { total }) }}
        </div>
    </div>
</template>

<style lang="scss">
    .pk-pagination {
        &__limit {
            font-size: var(--text-12);
            padding: 0 var(--spacing-12) 0 var(--spacing-8);
            background-image: var(--bg-chevron);
            background-repeat: no-repeat;
            background-position: right center;
            background-size: 1em;
            cursor: pointer;
            min-width: var(--spacing-40);

            &:disabled {
                cursor: not-allowed;
                opacity: var(--opacity-50);
            }
        }
    }
</style>
